import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface FrameworkData {
  name: string;
  compliance: number;
  target: number;
  risk: number;
}

interface FrameworkPerformanceChartProps {
  data: FrameworkData[];
  title?: string;
  height?: number;
  showTarget?: boolean;
  showRisk?: boolean;
}

const FrameworkPerformanceChart: React.FC<FrameworkPerformanceChartProps> = ({
  data,
  title = 'Framework Performance Overview',
  height = 300,
  showTarget = true,
  showRisk = true
}) => {
  const labels = data.map(item => item.name);

  const datasets = [
    {
      label: 'Compliance Score',
      data: data.map(item => item.compliance),
      backgroundColor: 'rgba(37, 99, 235, 0.8)',
      borderColor: '#2563eb',
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
    ...(showTarget ? [{
      label: 'Target Score',
      data: data.map(item => item.target),
      backgroundColor: 'rgba(16, 185, 129, 0.8)',
      borderColor: '#10b981',
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }] : []),
    ...(showRisk ? [{
      label: 'Risk Level',
      data: data.map(item => item.risk),
      backgroundColor: 'rgba(239, 68, 68, 0.8)',
      borderColor: '#ef4444',
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    }] : [])
  ];

  const chartData = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        }
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: 'bold' as const
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y + '%';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Frameworks',
          font: {
            size: 14,
            weight: 500
          }
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Score (%)',
          font: {
            size: 14,
            weight: 500
          }
        },
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          },
          font: {
            size: 12
          }
        }
      }
    },
    elements: {
      bar: {
        borderRadius: 4,
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div style={{ height: `${height}px` }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default FrameworkPerformanceChart;